import moment from "moment";

import { PRODUCT_SALES_GROUP_MAP } from "../../utils/constantFiles/constant";
import {
  findProductGroupDictEntry,
  isTyvasoProductSelection,
  productGroupKeysMatch,
} from "../MyPerformance/myPerformanceHelpers";

/** @typedef {import("./qbrTypes").QbrReportResponse} QbrReportResponse */
/** @typedef {import("./qbrTypes").PriorQuarterSnapshot} PriorQuarterSnapshot */
/** @typedef {import("./qbrTypes").TerritoryOverview} TerritoryOverview */

export const CURRENT_QUARTER_KEY = "current_quarter";

/** Format the API sends `refresh_timestamp` in, e.g. "2026-09-23 09:47:23". */
const REFRESH_TIMESTAMP_FORMAT = "YYYY-MM-DD HH:mm:ss";

const DEVICE_MIX_COLORS = ["#396D87", "#8FB8CC", "#00A63E", "#f1c40f"];
const INDICATION_MIX_COLORS = ["#e67e22", "#f1c40f", "#396D87", "#6B7280"];
const ATTAINMENT_COLORS = ["#396D87", "#E87722", "#00A63E", "#6B7280"];

/** Competitive Dynamics shows at most this many claimants. */
const MAX_COMPETITIVE_CLAIMANTS = 5;

const BREADTH_DEPTH_ORDER = [
  { key: "non_prescribers", fallbackLabel: "Non-Prescribers", color: "#6B7280" },
  { key: "breadth_writers", fallbackLabel: "Breadth Writers", color: "#e67e22" },
  { key: "depth_writers", fallbackLabel: "Depth Writers", color: "#00A63E" },
];

/** Display order for known products, e.g. Remodulin before Orenitram. */
const PRODUCT_DISPLAY_ORDER = [
  ...new Set(
    Object.values(PRODUCT_SALES_GROUP_MAP).map((entry) => entry.product),
  ),
];

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function titleCase(value) {
  const text = (value ?? "").toString().trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

function productOrderIndex(key) {
  const index = PRODUCT_DISPLAY_ORDER.findIndex((product) =>
    productGroupKeysMatch(product, key),
  );
  return index === -1 ? PRODUCT_DISPLAY_ORDER.length : index;
}

/** Sorts payload keys into display order, keeping unknown keys at the end. */
function sortProductKeys(keys) {
  return [...keys].sort((a, b) => {
    const byOrder = productOrderIndex(a) - productOrderIndex(b);
    return byOrder || a.localeCompare(b);
  });
}

/** Products the rep's group sells — fallback when the report has no rows. */
function productKeysForGroup(selectedProduct) {
  const key = (selectedProduct ?? "").toString().trim();
  if (!key) return [];

  const products = [];
  Object.entries(PRODUCT_SALES_GROUP_MAP).forEach(([mapKey, entry]) => {
    const belongsToSelection =
      productGroupKeysMatch(key, mapKey) ||
      productGroupKeysMatch(key, entry.salesGroup);
    if (belongsToSelection && !products.includes(entry.product)) {
      products.push(entry.product);
    }
  });

  return products.length ? products : [key];
}

function mapSplit(label, segments, palette) {
  const rows = Array.isArray(segments) ? segments : [];
  if (!rows.length) return null;

  return {
    label,
    segments: rows.map((segment, index) => ({
      name: segment?.label ?? "",
      value: toNumber(segment?.percentage),
      color: palette[index % palette.length],
    })),
  };
}

function hasSplitData(entry) {
  return Boolean(entry?.device_mix || entry?.indication_mix);
}

/**
 * key_metrics is keyed by ProductGroup (TYVASO / REMODULIN / ORENITRAM) while
 * selectedProduct can be a sales group (CPS / ALD / ROCS), so the report's own
 * product keys are tried before falling back to a flat payload.
 */
function findKeyMetricsBlock(keyMetrics, selectedProduct, productKeys) {
  for (const key of [selectedProduct, ...productKeys]) {
    const entry = findProductGroupDictEntry(keyMetrics, key);
    if (hasSplitData(entry)) return entry;
  }
  return hasSplitData(keyMetrics) ? keyMetrics : null;
}

/**
 * Default split fields: the Referrals QTD panel reads the non-"_active"
 * device/indication mixes. The Active Patients QTD panel passes the "_active"
 * variants instead — same block, same colors, same skip-if-absent handling.
 */
const REFERRAL_SPLIT_FIELDS = Object.freeze({
  device: "device_mix",
  indication: "indication_mix",
});
const ACTIVE_SPLIT_FIELDS = Object.freeze({
  device: "device_mix_active",
  indication: "indication_mix_active",
});

function mapProductSplits(
  keyMetrics,
  selectedProduct,
  productKeys,
  fields = REFERRAL_SPLIT_FIELDS,
) {
  if (!keyMetrics || typeof keyMetrics !== "object") return [];

  const block = findKeyMetricsBlock(keyMetrics, selectedProduct, productKeys);
  if (!block) return [];

  // PAH vs PH-ILD only splits Tyvaso; Remodulin and Orenitram are PAH-only.
  const hasIndicationSplit = productKeys.some(isTyvasoProductSelection);

  return [
    mapSplit("Method of Delivery Mix", block?.[fields.device], DEVICE_MIX_COLORS),
    hasIndicationSplit
      ? mapSplit("Indication Mix", block?.[fields.indication], INDICATION_MIX_COLORS)
      : null,
  ].filter(Boolean);
}

function mapBreadthDepth(lengthBreadth) {
  if (!lengthBreadth) return [];

  return BREADTH_DEPTH_ORDER.filter(({ key }) => lengthBreadth[key] != null).map(
    ({ key, fallbackLabel, color }) => ({
      label: lengthBreadth[key]?.label ?? fallbackLabel,
      count: toNumber(lengthBreadth[key]?.count),
      definition: lengthBreadth[key]?.criteria ?? "",
      color,
    }),
  );
}

/**
 * One Attainment QTD bar per key in the report — two for a ROCS rep
 * (REMODULIN + ORENITRAM), one for ALD / CPS (TYVASO).
 */
function mapAttainmentProducts(attainmentDict) {
  if (!attainmentDict || typeof attainmentDict !== "object") return [];

  return sortProductKeys(Object.keys(attainmentDict)).map((key, index) => ({
    key,
    name: titleCase(key),
    current: toNumber(attainmentDict[key]?.Actuals),
    goal: toNumber(attainmentDict[key]?.Goals),
    color: ATTAINMENT_COLORS[index % ATTAINMENT_COLORS.length],
  }));
}

/**
 * One Active Patients QTD stat per product, derived from the same
 * attainmentProducts list so the product set, display order and per-product
 * colors line up exactly with the Attainment bars. The count comes from
 * `attainment_qtd_performance_summary.<PRODUCT>.Active_Patients_Count`.
 */
function mapActivePatients(attainmentProducts, attainmentDict) {
  if (!attainmentDict || typeof attainmentDict !== "object") return [];

  return attainmentProducts.map((product) => ({
    product: product.name,
    count: toNumber(attainmentDict[product.key]?.Active_Patients_Count),
    color: product.color,
  }));
}

/**
 * Top writers with one referral column per product in the report. Columns
 * follow the payload's own keys, so they line up with whatever the API
 * returns.
 */
function mapTopWriters(advocatesDict) {
  if (!advocatesDict || typeof advocatesDict !== "object") {
    return { columns: [], rows: [] };
  }

  const columns = sortProductKeys(Object.keys(advocatesDict)).map((key) => ({
    key,
    label: titleCase(key),
  }));

  const rowsByHcp = new Map();
  columns.forEach((column) => {
    const block = advocatesDict[column.key];
    const rows = Array.isArray(block?.Increase) ? block.Increase : [];

    rows.forEach((row) => {
      const name = row?.HCP ?? "";
      if (!name) return;
      const existing = rowsByHcp.get(name) || { name, values: {}, rank: 0 };
      existing.values[column.key] = toNumber(row?.Change_in_Metric);
      existing.rank = existing.rank || toNumber(row?.RANK);
      rowsByHcp.set(name, existing);
    });
  });

  const total = (row) =>
    columns.reduce((sum, column) => sum + toNumber(row.values[column.key]), 0);

  const rows = [...rowsByHcp.values()]
    .sort((a, b) => total(b) - total(a) || a.rank - b.rank)
    .map(({ name, values }) => ({ name, values }));

  return { columns, rows };
}

const EMPTY_COMPETITIVE_DYNAMICS = { subtitle: "", claimants: [] };

/** Default-empty rep inputs shape — matches the backend's response contract. */
const EMPTY_REP_INPUTS = Object.freeze({
  payor_reimbursement_dynamics: "",
  measures_of_success: "",
});

/**
 * Project ``qbr_report.rep_inputs`` (set by the backend reader /
 * overlay-at-read in ``fetch_qbr_snapshot``) into the stable
 * ``{payor_reimbursement_dynamics, measures_of_success}`` shape consumed by
 * TerritoryOverviewCard's two free-text fields. Missing / malformed input
 * collapses to the default-empty shape so SectionInputField always sees two
 * string values regardless of edit history or snapshot vintage.
 */
function mapRepInputs(repInputs) {
  if (!repInputs || typeof repInputs !== "object") {
    return { ...EMPTY_REP_INPUTS };
  }
  return {
    payor_reimbursement_dynamics: String(
      repInputs.payor_reimbursement_dynamics ?? "",
    ),
    measures_of_success: String(repInputs.measures_of_success ?? ""),
  };
}

/**
 * The backend query filters to nbaidentifier = 'S10-B_Y' (Yutrepia), so the
 * subtitle is fixed accordingly. Empty / missing results return an empty
 * structure so TerritoryOverviewCard shows its "No insights found" state.
 */
function mapCompetitiveDynamics(competitiveData) {
  if (!Array.isArray(competitiveData) || !competitiveData.length) {
    return EMPTY_COMPETITIVE_DYNAMICS;
  }

  return {
    subtitle: "Top Yutrepia claimants this quarter",
    claimants: competitiveData
      .slice(0, MAX_COMPETITIVE_CLAIMANTS)
      .map((row) => {
        const rawName = (row?.HCP_Name ?? "").toString().trim();
        return {
          name: rawName ? `Dr. ${rawName}` : "",
          claims: toNumber(row?.New_Claims),
          note: row?.Details ?? "",
        };
      }),
  };
}

/** "Q32026" (API compact) or "Q3 2026" -> "Q3 2026". */
export function formatQuarterLabel(rawQuarter) {
  const text = (rawQuarter ?? "").toString().trim();
  const match = /^Q([1-4])\s*(\d{4})$/i.exec(text);
  return match ? `Q${match[1]} ${match[2]}` : text;
}

/** Sort key so Q4 2026 > Q3 2026 > Q4 2025. Unknown ids sort last. */
function compactQuarterRank(quarterId) {
  const compact = (quarterId ?? "").toString().replace(/\s+/g, "");
  const match = /^Q([1-4])(\d{4})$/i.exec(compact);
  if (!match) return 0;
  return Number(match[2]) * 10 + Number(match[1]);
}

/**
 * Compact quarter ids from `prior_quarters`, most recent first.
 * Does not assume a single entry.
 * @param {Record<string, PriorQuarterSnapshot>|null|undefined} priorQuarters
 * @returns {string[]}
 */
export function priorQuarterIdsMostRecentFirst(priorQuarters) {
  if (!priorQuarters || typeof priorQuarters !== "object") return [];
  return Object.keys(priorQuarters).sort(
    (a, b) => compactQuarterRank(b) - compactQuarterRank(a),
  );
}

/**
 * Picks the six-section report for the selected dropdown key. The current
 * quarter reads the live root envelope and its `refresh_timestamp`; a prior
 * quarter reads `prior_quarters` and is stamped with when the snapshot job
 * captured it.
 *
 * @returns {{ report: import("./qbrTypes").QbrReport, refreshTimestamp: string }}
 */
function resolveQuarterSlice(response, quarterKey) {
  const empty = { report: {}, refreshTimestamp: "" };
  if (!response || typeof response !== "object") return empty;

  if (!quarterKey || quarterKey === CURRENT_QUARTER_KEY) {
    return {
      report: response.qbr_report ?? {},
      refreshTimestamp: response.refresh_timestamp ?? "",
    };
  }

  const prior = response.prior_quarters?.[quarterKey];
  if (!prior || typeof prior !== "object") return empty;

  return {
    report: prior.qbr_report ?? {},
    refreshTimestamp:
      prior.snapshot_meta?.captured_at ?? response.refresh_timestamp ?? "",
  };
}

/** @returns {string} Display timestamp, or "" when the API omits it. */
function formatRefreshTimestamp(rawTimestamp) {
  const text = (rawTimestamp ?? "").toString().trim();
  if (!text) return "";

  const parsedStrict = moment(text, REFRESH_TIMESTAMP_FORMAT, true);
  if (parsedStrict.isValid()) {
    return parsedStrict.format("MMM D, YYYY h:mm A");
  }
  const parsedIso = moment(text);
  return parsedIso.isValid() ? parsedIso.format("MMM D, YYYY h:mm A") : text;
}

/**
 * Maps the `/kpi_metrics/qbr_report` payload onto the TerritoryOverview shape
 * consumed by TerritoryOverviewCard. All six sections
 * (attainment_qtd_performance_summary, territory_goal, key_metrics,
 * length_breadth, top_product_advocates_growers, competitive_dynamics) come
 * from the API; a missing or empty section maps to an empty structure so the
 * card renders its "No insights found" state.
 *
 * @param {object} params
 * @param {QbrReportResponse|null} params.qbrReportResponse Raw thunk payload.
 * @param {string} params.selectedProduct Product group whose block to read.
 * @param {string} [params.quarterKey] `current_quarter` or a compact prior id.
 * @returns {TerritoryOverview}
 */
export function mapQbrReportToTerritoryOverview({
  qbrReportResponse,
  selectedProduct,
  quarterKey = CURRENT_QUARTER_KEY,
}) {
  const { report, refreshTimestamp } = resolveQuarterSlice(
    qbrReportResponse,
    quarterKey,
  );

  const attainmentProducts = mapAttainmentProducts(
    report.attainment_qtd_performance_summary,
  );
  // Attainment is live, so its keys are the source of truth for which
  // products this territory sells; fall back to the rep's assigned group.
  const productKeys = attainmentProducts.length
    ? attainmentProducts.map((product) => product.key)
    : productKeysForGroup(selectedProduct);

  const territoryGoals = report.territory_goal ?? {};
  const quarterlyGoalTotal = productKeys.reduce(
    (sum, key) => sum + toNumber(findProductGroupDictEntry(territoryGoals, key)?.Goals),
    0,
  );

  return {
    lastRefreshTime: formatRefreshTimestamp(refreshTimestamp),
    quarterlyGoal: `${quarterlyGoalTotal} New Starts`,
    attainmentCurrent: attainmentProducts.reduce(
      (sum, product) => sum + product.current,
      0,
    ),
    attainmentGoal: attainmentProducts.reduce(
      (sum, product) => sum + product.goal,
      0,
    ),
    attainmentProducts,
    activePatients: mapActivePatients(
      attainmentProducts,
      report.attainment_qtd_performance_summary,
    ),
    topWriters: mapTopWriters(report.top_product_advocates_growers),
    breadthDepth: mapBreadthDepth(report.length_breadth),
    // Referrals QTD splits — non-"_active" mixes (unchanged).
    referralSplits: mapProductSplits(
      report.key_metrics,
      selectedProduct,
      productKeys,
    ),
    // Active Patients QTD splits — "_active" mixes, skip-if-absent per product.
    activePatientSplits: mapProductSplits(
      report.key_metrics,
      selectedProduct,
      productKeys,
      ACTIVE_SPLIT_FIELDS,
    ),
    competitiveDynamics: mapCompetitiveDynamics(report.competitive_dynamics),
    repInputs: mapRepInputs(report.rep_inputs),
  };
}

export default mapQbrReportToTerritoryOverview;
